import { ButtonType } from "@components/atoms/Button/types";
import React, { useEffect, useState } from "react";
import { Button, Spacer, VDBText } from "@components/atoms";
import Tooltip from "@components/atoms/Tooltip";
import styled, { DefaultTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import { useRoute } from "@react-navigation/native";
import { getRequestType, isPendingRequestItem } from "./utils";
import Modal from "@components/atoms/Modal";
import RequestChat from "./RequestChat";
import RequestConfirmPopup from "./RequestConfirmPopup";
import { ActiveClickTypes } from "../types";
import { useTranslation } from "react-i18next";
import { VDBIcons } from "@utils/VDBIcons";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Platform, View } from "react-native";
import TooltipButton from "@scenes/pdp/utils/TooltipButton";
import { css } from "styled-components/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSelectedItems } from "../../../contexts/SelectedItemsContext";

const QuoteDisabledTooltip = ({
  tooltip,
  children,
}: {
  tooltip: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const [isOpen, setIsOpen] = useState(false);
  const openOnPress = theme.isMobile || Platform.OS !== "web";

  if (!openOnPress) {
    return (
      <TooltipButton
        enableTooltip
        position="top"
        width="280px"
        tooltip={tooltip}
      >
        {children}
      </TooltipButton>
    );
  }

  return (
    <Tooltip
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      showOnPress
      placement="top"
      alignment="center"
      tooltipBackgroundColor={theme["core-06"]}
      popoverBackdropColor="transparent"
      popoverContentProps={{ width: 280, maxWidth: 280 }}
      trigger={() => (
        <View pointerEvents="none" style={{ width: "100%" }}>
          {children}
        </View>
      )}
    >
      <VDBText style={typography["Minor-R"]} color={theme["core-01"]}>
        {tooltip}
      </VDBText>
    </Tooltip>
  );
};

type ButtonsContainerProps = {
  stockItemRequest: StockItemRequestClass;
  productType: any;
  onSubmitChange: () => void;
  modifyRequest: any;
  disabledButton?: boolean;
  selectedItems?: any;
  isHideOtherButtonVisible?: boolean;
  isBuilderItem?: boolean;
  showOrderCartQuoteSenderActions?: boolean;
  showOrderCartQuoteReceiverActions?: boolean;
};

const ButtonsContainer = ({
  stockItemRequest,
  productType,
  onSubmitChange,
  modifyRequest,
  disabledButton = false,
  isHideOtherButtonVisible,
  isBuilderItem,
  showOrderCartQuoteSenderActions = false,
  showOrderCartQuoteReceiverActions = false,
}: ButtonsContainerProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const requestType = getRequestType(stockItemRequest.request_type);
  const route = useRoute();
  const [openChat, setOpenChat] = useState(route.params?.openChat ?? false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [isDisabledButton, setDisabledButton] = useState(disabledButton);
  const { selectedItems: quoteSelectedItems } = useSelectedItems();
  const hasQuoteSelection = (quoteSelectedItems ?? []).length > 0;
  const selectableQuoteItemIds =
    stockItemRequest?.children
      ?.filter((item) => isPendingRequestItem(item))
      .map((item) => item.id) ?? [];
  const allQuoteItemsSelected =
    selectableQuoteItemIds.length > 0 &&
    selectableQuoteItemIds.every((id) =>
      (quoteSelectedItems ?? []).includes(id)
    );
  const withdrawDeclineDisabled = selectableQuoteItemIds.length
    ? !allQuoteItemsSelected
    : !hasQuoteSelection;
  const { bottom } = useSafeAreaInsets();
  let recipientEmailAvailable =
    !stockItemRequest?.quote_details?.is_user_registered &&
    stockItemRequest.view_type === "buyer_view";

  let showChatText =
    requestType === "Quote" && stockItemRequest.view_type === "buyer_view";
  const trackStatus = String(
    stockItemRequest?.track_status_key ?? stockItemRequest?.track_status ?? ""
  )
    .trim()
    .toLowerCase();
  const isWholeRequestPending = trackStatus
    ? trackStatus === "pending"
    : String(stockItemRequest?.status ?? "").toLowerCase() === "pending";

  useEffect(() => {
    setDisabledButton(disabledButton);
  }, [disabledButton]);

  const [showPopup, setShowPopup] = useState(false);
  const [activeClick, setActiveClick] = useState<ActiveClickTypes>(
    ActiveClickTypes.DECLINE
  );
  const buttonWidth = theme.isMobile ? "auto" : theme.isLargeScreen ? 224 : 160;

  const showConfirmPopup = (click: ActiveClickTypes) => {
    setShowPopup(true);
    setActiveClick(click);
  };

  useEffect(() => {
    setOpenChat(route?.params?.openChat ?? false);
  }, [route?.params]);

  useEffect(() => {
    if (openChat) {
      setShowMessageModal(true);
    }
  }, [openChat]);

  const DeclineButton = (declineProps: any) => {
    const { type, disabled } = declineProps;
    const isDeclineDisabled = disabled ?? isDisabledButton;
    const button = (
      <Button
        type={type || ButtonType.primary}
        width={buttonWidth}
        category={productType.theme}
        title={t("requestHistoryDetail.buttons.decline.text")}
        disabled={isDeclineDisabled}
        onPress={() => {
          showConfirmPopup(ActiveClickTypes.DECLINE);
        }}
      />
    );
    if (
      !isDeclineDisabled ||
      (requestType !== "Quote" && !showOrderCartQuoteReceiverActions)
    ) {
      return button;
    }
    return (
      <QuoteDisabledTooltip
        tooltip={t(
          "requestHistoryDetail.buttons.decline.disabledTooltip.text",
          "The decline applies to the entire Quote, with all items being declined at once."
        )}
      >
        {button}
      </QuoteDisabledTooltip>
    );
  };

  const WithdrawButton = (declineProps: any) => {
    const { type, disabled } = declineProps;
    const isWithdrawDisabled = disabled ?? isDisabledButton;
    const button = (
      <Button
        type={type || ButtonType.secondary}
        width={buttonWidth}
        category={productType.theme}
        title={t("requestHistoryDetail.buttons.withdraw.text")}
        disabled={isWithdrawDisabled}
        onPress={() => {
          showConfirmPopup(ActiveClickTypes.WITHDRAW);
        }}
      />
    );
    if (!isWithdrawDisabled || requestType !== "Quote") {
      return button;
    }
    return (
      <QuoteDisabledTooltip
        tooltip={t(
          "requestHistoryDetail.buttons.withdraw.disabledTooltip.text",
          "The withdrawal applies to the entire Quote, with all items being withdrawn at once."
        )}
      >
        {button}
      </QuoteDisabledTooltip>
    );
  };

  const AcceptButton = (acceptProps: any) => {
    const { type } = acceptProps;
    return (
      <Button
        type={type || ButtonType.primary}
        width={buttonWidth}
        category={productType.theme}
        disabled={isDisabledButton}
        title={
          recipientEmailAvailable || showOrderCartQuoteSenderActions
            ? t("requestHistoryDetail.buttons.acceptBehalf.text")
            : t("requestHistoryDetail.buttons.accept.text")
        }
        onPress={() => {
          showConfirmPopup(
            requestType === "Quote"
              ? ActiveClickTypes.ACCEPTQUOTE
              : ActiveClickTypes.ACCEPT
          );
        }}
      />
    );
  };

  const ModifyButton = (modifyProps: any) => {
    const { type, modifyRequest: onModifyRequest } = modifyProps;
    return (
      <Button
        type={type || ButtonType.tertiary}
        width={buttonWidth}
        category={productType.theme}
        title={t("requestHistoryDetail.buttons.modify.text")}
        onPress={onModifyRequest}
      />
    );
  };

  const MessageButton = (messageProps: any) => {
    const { type, customText, fullWidth } = messageProps;
    return (
      <Button
        type={type ?? ButtonType.secondary}
        width={fullWidth ? undefined : buttonWidth}
        category={productType.theme}
        showIcon={true}
        hideText={true}
        showLeftText={true}
        iconSource={VDBIcons.MESSAGE}
        iconColor={
          type === ButtonType.primary
            ? (theme[`${productType.theme}-05` as keyof DefaultTheme] as string)
            : (theme[`${productType.theme}-06` as keyof DefaultTheme] as string)
        }
        title={t(
          customText ? customText : "requestHistoryDetail.buttons.message.text",
          customText ? "Chat" : undefined
        )}
        onPress={() => {
          setShowMessageModal(true);
        }}
      />
    );
  };

  const ButtonContent = (
    <>
      <ButtonContainer>
        <ButtonFlexContainer>
          <AcceptButton type={ButtonType.primary} />
        </ButtonFlexContainer>
      </ButtonContainer>
      <Spacer size={SpaceType.Big} />
    </>
  );

  const Wrapper = theme.isMobile ? View : ButtonFlexContainer;
  const bottomPadding = theme.isMobile ? bottom : SpaceType.Big;
  if (showOrderCartQuoteReceiverActions) {
    if (!isWholeRequestPending) return null;
    return (
      <>
        <ReceiverQuoteButtonRow paddingBottom={bottomPadding}>
          <ButtonFlexContainer fillHeight={false}>
            <DeclineButton
              type={ButtonType.tertiary}
              disabled={withdrawDeclineDisabled}
            />
          </ButtonFlexContainer>
          <SenderQuoteActions>
            <ButtonFlexContainer fillHeight={false}>
              <Button
                type={ButtonType.primary}
                width={buttonWidth}
                category={productType.theme}
                preserveCategory
                title={t("requestHistoryDetail.buttons.accept.text", "Accept")}
                disabled={!hasQuoteSelection}
                onPress={() => showConfirmPopup(ActiveClickTypes.ACCEPTQUOTE)}
              />
            </ButtonFlexContainer>
          </SenderQuoteActions>
        </ReceiverQuoteButtonRow>
        {showPopup && (
          <RequestConfirmPopup
            stockItemRequest={stockItemRequest}
            activeClick={activeClick}
            onPopupShowChange={setShowPopup}
            onSubmitChange={onSubmitChange}
            orderLineActions
          />
        )}
      </>
    );
  }
  return (
    <>
      {stockItemRequest.view_type === "buyer_view" ? (
        requestType === "Buy" ? (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : "center"
              }
              width={"100%"}
            >
              {stockItemRequest.status === "pending" && !isBuilderItem && (
                <ButtonFlexContainer>
                  <ModifyButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}

              {stockItemRequest.status === "pending" && isBuilderItem && (
                <ButtonFlexContainer>
                  <WithdrawButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Memo" ? (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : "center"
              }
              width={"100%"}
            >
              {stockItemRequest.status === "pending" && (
                <ButtonFlexContainer>
                  <ModifyButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Hold" ? (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : "center"
              }
              width={"100%"}
            >
              {stockItemRequest.status === "pending" && (
                <ButtonFlexContainer>
                  <ModifyButton
                    type={ButtonType.tertiary}
                    modifyRequest={modifyRequest}
                  />
                </ButtonFlexContainer>
              )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Quote" && showOrderCartQuoteSenderActions ? (
          isWholeRequestPending ? (
            <SenderQuoteButtonRow paddingBottom={bottomPadding}>
              <ButtonFlexContainer fillHeight={false}>
                <WithdrawButton
                  type={ButtonType.tertiary}
                  disabled={withdrawDeclineDisabled}
                />
              </ButtonFlexContainer>
              <SenderQuoteActions>
                <ButtonFlexContainer fillHeight={false}>
                  <AcceptButton type={ButtonType.primary} />
                </ButtonFlexContainer>
              </SenderQuoteActions>
            </SenderQuoteButtonRow>
          ) : null
        ) : requestType === "Quote" ? (
          <ButtonQuoteView paddingBottom={bottomPadding}>
            <ButtonContainerView
              justifyContent={
                stockItemRequest.status === "pending" &&
                !isHideOtherButtonVisible
                  ? "space-between"
                  : theme.isLargeScreen
                  ? "center"
                  : "space-evenly"
              }
            >
              <Wrapper>
                {stockItemRequest.status === "pending" &&
                  !isHideOtherButtonVisible &&
                  recipientEmailAvailable &&
                  (theme.isMobile ? (
                    ButtonContent
                  ) : (
                    <ButtonFlexContainer>{ButtonContent}</ButtonFlexContainer>
                  ))}
              </Wrapper>
              <ButtonContainer
                justifyContent={
                  stockItemRequest.status === "pending" &&
                  !isHideOtherButtonVisible
                    ? "space-between"
                    : theme.isLargeScreen
                    ? "center"
                    : "space-evenly"
                }
                allowFlex={
                  recipientEmailAvailable || theme.isMobile ? false : true
                }
              >
                {stockItemRequest.status === "pending" &&
                  !isHideOtherButtonVisible && (
                    <ButtonFlexContainer>
                      <WithdrawButton
                        type={ButtonType.tertiary}
                        disabled={
                          selectableQuoteItemIds.length > 0
                            ? !allQuoteItemsSelected
                            : isDisabledButton
                        }
                        modifyRequest={modifyRequest}
                      />
                    </ButtonFlexContainer>
                  )}
                <ButtonFlexContainer>
                  <MessageButton
                    type={ButtonType.secondary}
                    customText={
                      showChatText
                        ? "requestHistoryDetail.buttons.chat.text"
                        : undefined
                    }
                  />
                </ButtonFlexContainer>
              </ButtonContainer>
            </ButtonContainerView>
            {stockItemRequest.status === "pending" &&
              !isHideOtherButtonVisible &&
              recipientEmailAvailable && (
                <>
                  {theme.isMobile && <Spacer size={SpaceType.Big} />}
                  <VDBText
                    type={typography["Minor-R"]}
                    color={theme["core-06"]}
                  >
                    {t("requestHistoryDetail.acceptBehalf.screenText.text")}
                  </VDBText>
                </>
              )}
          </ButtonQuoteView>
        ) : (
          <ButtonView paddingBottom={bottomPadding}>
            <ButtonContainer justifyContent={"center"} width={"100%"}>
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.primary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        )
      ) : stockItemRequest.view_type === "seller_view" ? (
        ["Buy", "Memo", "Hold", "Questions"].includes(requestType) ? (
          <ButtonView
            justifyContent={
              stockItemRequest.status === "pending"
                ? "space-between"
                : "space-evenly"
            }
            paddingBottom={bottomPadding}
          >
            {stockItemRequest.status === "pending" && (
              <>
                <ButtonContainer>
                  <ButtonFlexContainer>
                    <AcceptButton type={ButtonType.primary} />
                  </ButtonFlexContainer>
                </ButtonContainer>
                <Spacer size={SpaceType.Big} />
              </>
            )}
            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : theme.isLargeScreen
                  ? "center"
                  : "space-evenly"
              }
            >
              {stockItemRequest.status === "pending" &&
                !isHideOtherButtonVisible && (
                  <ButtonFlexContainer>
                    <DeclineButton type={ButtonType.tertiary} />
                  </ButtonFlexContainer>
                )}
              <ButtonFlexContainer>
                <MessageButton
                  type={
                    stockItemRequest.status === "pending"
                      ? ButtonType.secondary
                      : ButtonType.primary
                  }
                />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : requestType === "Quote" ? (
          <ButtonView
            justifyContent={
              stockItemRequest.status === "pending" && !isHideOtherButtonVisible
                ? "space-between"
                : "space-evenly"
            }
            paddingBottom={bottomPadding}
          >
            {stockItemRequest.status === "pending" &&
              !isHideOtherButtonVisible && (
                <>
                  <ButtonContainer>
                    <ButtonFlexContainer>
                      <AcceptButton type={ButtonType.primary} />
                    </ButtonFlexContainer>
                  </ButtonContainer>
                  <Spacer size={SpaceType.Big} />
                </>
              )}

            <ButtonContainer
              justifyContent={
                stockItemRequest.status === "pending"
                  ? "space-between"
                  : theme.isLargeScreen
                  ? "center"
                  : "space-evenly"
              }
            >
              {stockItemRequest.status === "pending" &&
                !isHideOtherButtonVisible && (
                  <ButtonFlexContainer>
                    <DeclineButton
                      type={ButtonType.tertiary}
                      disabled={
                        selectableQuoteItemIds.length > 0
                          ? !allQuoteItemsSelected
                          : isDisabledButton
                      }
                      modifyRequest={modifyRequest}
                    />
                  </ButtonFlexContainer>
                )}
              <ButtonFlexContainer>
                <MessageButton type={ButtonType.secondary} />
              </ButtonFlexContainer>
            </ButtonContainer>
          </ButtonView>
        ) : (
          <></>
        )
      ) : (
        <></>
      )}
      {showPopup && (
        <RequestConfirmPopup
          stockItemRequest={stockItemRequest}
          activeClick={activeClick}
          onPopupShowChange={setShowPopup}
          onSubmitChange={onSubmitChange}
          orderLineActions={showOrderCartQuoteSenderActions}
        />
      )}
      {showMessageModal && (
        <Modal
          headerTitle={t("requestHistoryDetail.requestChat.modal.title.text")}
          isOpen={showMessageModal}
          onClose={() => {
            setShowMessageModal(false);
            setOpenChat(false);
          }}
          paddingLeft={0}
          paddingRight={0}
          paddingTop={0}
          paddingBottom={0}
          skipKeyboardAware={true}
          size={"md"}
        >
          <RequestChat stockItemRequest={stockItemRequest} />
        </Modal>
      )}
    </>
  );
};

const ButtonView = styled.View<{
  justifyContent?: string;
  paddingBottom?: number;
}>`
  padding: ${({ paddingBottom = 0 }) =>
    css`
      ${SpaceType.Basic}px ${SpaceType.Double}px ${paddingBottom}px ${SpaceType.Double}px
    `};
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row-reverse")};
  justify-content: ${({ justifyContent }) => justifyContent ?? "space-evenly"};
  border-radius: ${({ theme }) => (theme.isMobile ? "0" : "8px")};
`;

const ButtonContainerView = styled.View<{ justifyContent?: string }>`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row-reverse")};
  justify-content: ${({ justifyContent }) => justifyContent ?? "space-evenly"};
  border-radius: ${({ theme }) => (theme.isMobile ? "0" : "8px")};
`;

const ReceiverQuoteButtonRow = styled.View<{ paddingBottom?: number }>`
  padding: ${({ paddingBottom = 0 }) =>
    `${SpaceType.Big}px ${SpaceType.Double}px ${paddingBottom}px ${SpaceType.Double}px`};
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: ${({ theme }) => (theme.isMobile ? "column-reverse" : "row")};
  justify-content: space-between;
  align-items: ${({ theme }) => (theme.isMobile ? "stretch" : "center")};
  gap: ${SpaceType.Basic}px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${({ theme }) => theme["core-03"]};
`;

const SenderQuoteButtonRow = styled.View<{ paddingBottom?: number }>`
  padding: ${({ paddingBottom = 0 }) =>
    `${SpaceType.Big}px ${SpaceType.Double}px ${paddingBottom}px ${SpaceType.Double}px`};
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: ${({ theme }) => (theme.isMobile ? "column-reverse" : "row")};
  justify-content: space-between;
  align-items: ${({ theme }) => (theme.isMobile ? "stretch" : "center")};
  gap: ${SpaceType.Basic}px;
  border-radius: ${({ theme }) => (theme.isMobile ? "0" : "8px")};
`;

const SenderQuoteActions = styled.View`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row")};
  align-items: ${({ theme }) => (theme.isMobile ? "stretch" : "center")};
  gap: ${SpaceType.Basic}px;
`;

const ButtonQuoteView = styled.View<{
  justifyContent?: string;
  paddingBottom?: number;
}>`
  padding: ${({ paddingBottom = 0 }) =>
    `${SpaceType.Big}px ${SpaceType.Double}px ${paddingBottom}px ${SpaceType.Double}px`};
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: "column";
`;

const ButtonContainer = styled.View<{
  justifyContent?: string;
  width?: string;
  allowFlex?: boolean;
}>`
  flex-direction: row;
  ${({ allowFlex }) =>
    allowFlex &&
    css`
      flex: 1;
    `};
  justify-content: ${({ justifyContent }) => justifyContent ?? "space-between"};
  width: ${({ width }) => width ?? "unset"};
  gap: ${SpaceType.Basic}px;
`;

const ButtonFlexContainer = styled.View<{ fillHeight?: boolean }>`
  flex: ${({
    theme,
    fillHeight,
  }: {
    theme: DefaultTheme;
    fillHeight?: boolean;
  }) => (fillHeight === false ? "none" : theme.isMobile ? "1" : "none")};
  width: ${({
    theme,
    fillHeight,
  }: {
    theme: DefaultTheme;
    fillHeight?: boolean;
  }) => (fillHeight === false && theme.isMobile ? "100%" : "auto")};
`;

export default ButtonsContainer;
